import type { ToastPosition } from "@lib/config/toastConfig";
import { useToastStore } from "@lib/stores/toast";
import { cn } from "@lib/utils/cn";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ToastCard } from "./ToastCard";

const POSITION_CLASSES: Record<ToastPosition, string> = {
  "top-left": "toast-position toast-top-left",
  "top-right": "toast-position toast-top-right",
  "top-center": "toast-position toast-top-center",
  "bottom-left": "toast-position toast-bottom-left",
  "bottom-right": "toast-position toast-bottom-right",
  "bottom-center": "toast-position toast-bottom-center",
};

export function ToastContainer({ position }: { position: ToastPosition }) {
  // Select the raw array (stable reference) — filtering in the selector would
  // create a new array every render and cause an infinite re-render loop.
  const allToasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);
  const [expanded, setExpanded] = useState(false);

  const toasts = useMemo(
    () => allToasts.filter((t) => t.position === position),
    [allToasts, position],
  );

  // Escape dismisses the most recent toast
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && toasts.length > 0) {
        const last = toasts[toasts.length - 1];
        removeToast(last.id);
      }
    },
    [toasts, removeToast],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className={cn(POSITION_CLASSES[position], expanded && "toast-stack-expanded")}
      role="region"
      aria-label="Notifications"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      {toasts.map((toast, index) => (
        <div key={toast.id} style={{ "--depth": Math.min(index, 2) } as React.CSSProperties}>
          <ToastCard toast={toast} />
        </div>
      ))}
    </div>
  );
}
